import { Download, HouseSimple, Lock, SidebarSimple } from "@phosphor-icons/react";
import { Button, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { saveAs } from "file-saver";
import { useState } from "react";
import { Link } from "react-router";

import { PaymentModal } from "@/client/components/payment/payment-modal";
import { useSubscription } from "@/client/hooks/use-subscription";
import { usePrintResume } from "@/client/services/resume/print";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

const DOWNLOAD_BUTTON_LABEL = "Download Resume";

export const BuilderHeader = () => {
  const title = useResumeStore((state) => state.resume.title);
  const resumeId = useResumeStore((state) => state.resume.id);
  const locked = useResumeStore((state) => state.resume.locked);

  const toggle = useBuilderStore((state) => state.toggle);
  const isDragging = useBuilderStore(
    (state) => state.panel.left.handle.isDragging || state.panel.right.handle.isDragging,
  );
  const leftPanelSize = useBuilderStore((state) => state.panel.left.size);
  const rightPanelSize = useBuilderStore((state) => state.panel.right.size);

  const { canDownload: checkCanDownload } = useSubscription();
  const { printResume, loading: printLoading } = usePrintResume();
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const onToggle = (side: "left" | "right") => {
    toggle(side);
  };

  const handleDownload = async () => {
    const canDownload = await checkCanDownload(resumeId);
    if (!canDownload) {
      setShowPaymentModal(true);
      return;
    }

    try {
      const blob = await printResume({ id: resumeId });
      saveAs(blob, `${title}.pdf`);
    } catch {
      // Error is handled by the hook
    }
  };

  return (
    <div
      style={{ left: `${leftPanelSize}%`, right: `${rightPanelSize}%` }}
      className={cn(
        "fixed inset-x-0 top-0 z-[60] h-16 bg-secondary-accent/50 backdrop-blur-lg lg:z-20",
        !isDragging && "transition-[left,right]",
      )}
    >
      <div className="flex h-full items-center justify-between px-4">
        <Button
          size="icon"
          variant="ghost"
          className="flex"
          onClick={() => {
            onToggle("left");
          }}
        >
          <SidebarSimple />
        </Button>

        <div className="flex items-center justify-center gap-x-1 lg:mx-auto">
          <Button asChild size="icon" variant="ghost">
            <Link to="/dashboard/resumes">
              <HouseSimple />
            </Link>
          </Button>

          <span className="mr-2 text-xs opacity-40">{"/"}</span>

          <h1 className="font-medium">{title}</h1>

          {locked && (
            <Tooltip content="This resume is locked, please unlock to make further changes.">
              <Lock size={14} className="ml-2 opacity-75" />
            </Tooltip>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            disabled={printLoading}
            className="bg-accent text-accent-foreground hover:bg-accent-accent"
            size="sm"
            onClick={handleDownload}
          >
            <Download className="mr-2 size-4" />
            {DOWNLOAD_BUTTON_LABEL}
          </Button>

          <Button
            size="icon"
            variant="ghost"
            className="flex"
            onClick={() => {
              onToggle("right");
            }}
          >
            <SidebarSimple className="-scale-x-100" />
          </Button>
        </div>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
        }}
        onSuccess={() => {
          // Refresh subscription and retry download
          void handleDownload();
        }}
      />
    </div>
  );
};
